import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { baseQuery } from '../../../utils';
import type {
  SearchHSNQuery,
  SearchHSNQueryVariables,
  CreateHSNSACMasterMutation,
  CreateHSNSACMasterMutationVariables,
} from '../../../graphql';
import { searchHSN, createHSNSACMaster } from '../../../graphql';
import { RTK_CACHE_LIFETIME } from '../../../config';

export type SearchHSNResponse = {
  label: string;
  value: string;
  companyID?: number | null;
  hsnID?: number | null;
  codetype?: string | null;
  description?: string | null;
  raw?: any; // Raw API response item for edit/delete operations
}[];

export const searchHSNApi = createApi({
  reducerPath: 'hsnSACCodeApi',
  baseQuery: baseQuery,
  tagTypes: ['searchHSN'],
  endpoints: (builder) => ({
    searchHSN: builder.query<SearchHSNResponse, SearchHSNQueryVariables>({
      query: (variables) => ({ body: searchHSN, variables }),
      keepUnusedDataFor: RTK_CACHE_LIFETIME,
      providesTags: (result, error, { codetype }) => [
        { type: 'searchHSN', id: codetype || 'all' },
      ],
      transformResponse: (response: SearchHSNQuery, meta, variables) => {
        // Filter and transform the response based on codetype
        // codetype logic: Page === "NONITEM" ? "" : (Type === "GOODS" ? "HSN" : Type === "SERVICE" ? "SAC" : "")
        let filteredItems = response?.searchHSN || [];
        
        // If codetype is provided, ensure we only return items matching that type
        // (Backend should already filter, but this adds an extra safety check)
        if (variables?.codetype && variables.codetype !== '') {
          filteredItems = filteredItems.filter(
            (item) => item?.codetype?.toUpperCase() === variables.codetype.toUpperCase()
          );
        }
        
        return filteredItems.map((item) => ({
          label: `${item?.hsnsaccode || ''} - ${item?.description || ''}`,
          value: `${item?.hsnsaccode || ''}`,
          companyID: item?.companyid || null,
          hsnID: item?.hsnsacid || null,
          codetype: item?.codetype || null,
          description: item?.description || null,
          raw: item || undefined, // Include raw API data for edit/delete operations
        }));
      },
    }),
    createHSNSACMaster: builder.mutation<
      CreateHSNSACMasterMutation,
      CreateHSNSACMasterMutationVariables
    >({
      query: (variables) => ({
        body: createHSNSACMaster,
        variables,
      }),
      invalidatesTags: ['searchHSN'],
    }),
  }),
});

export const {
  useSearchHSNQuery,
  useLazySearchHSNQuery,
  usePrefetch,
  useCreateHSNSACMasterMutation,
} = searchHSNApi;

